<script setup lang="ts">
import type { CategoryFilter, CategoryFiltersState } from '~/types/database'

interface DynamicOption {
  value: string
  label: string
  count?: number
}

const props = defineProps<{
  filters: CategoryFilter[]
  modelValue: CategoryFiltersState
  hasActiveFilters: boolean
  categoryId: string
  dynamicOptions?: Record<string, DynamicOption[]>
  loadingOptions?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: CategoryFiltersState]
  'update:filter': [key: string, value: unknown, resetChildren: boolean]
  clear: []
}>()

// Mobile drawer state
const isDrawerOpen = ref(false)

// Sort filters by display_order for hierarchy
const sortedFilters = computed(() => {
  return [...props.filters].sort((a, b) => a.display_order - b.display_order)
})

/**
 * Get child filter keys for a given filter (filters with higher display_order)
 */
const getChildFilterKeys = (filterKey: string): string[] => {
  const filterIndex = sortedFilters.value.findIndex(f => f.filter_key === filterKey)
  if (filterIndex === -1) return []
  return sortedFilters.value.slice(filterIndex + 1).map(f => f.filter_key)
}

/**
 * Check if filter has children
 */
const hasChildren = (filterKey: string): boolean => {
  return getChildFilterKeys(filterKey).length > 0
}

/**
 * Update a single filter value with optional child reset
 */
const updateFilter = (key: string, value: unknown) => {
  const shouldResetChildren = hasChildren(key)

  // Build new filters state
  const newFilters = { ...props.modelValue }

  // Clear the current filter if value is empty
  if (value === null || value === undefined || value === '' || value === 'all') {
    delete newFilters[key]
  } else {
    newFilters[key] = value as string | string[] | boolean
  }

  // If this filter has children, reset them
  if (shouldResetChildren) {
    const childKeys = getChildFilterKeys(key)
    for (const childKey of childKeys) {
      delete newFilters[childKey]
    }
  }

  emit('update:modelValue', newFilters)
  emit('update:filter', key, value, shouldResetChildren)
}

/**
 * Get options for a filter - use dynamic options if available, fallback to static
 */
const getFilterOptions = (filter: CategoryFilter): DynamicOption[] => {
  const dynamicOpts = props.dynamicOptions?.[filter.filter_key]

  // Build "all" option
  const allOption: DynamicOption = {
    value: 'all',
    label: filter.placeholder || 'Todas las categorías'
  }

  if (dynamicOpts && dynamicOpts.length > 0) {
    // Use dynamic options with counts
    return [
      allOption,
      ...dynamicOpts.map(opt => ({
        value: opt.value,
        label: opt.count ? `${opt.label} (${opt.count})` : opt.label,
        count: opt.count
      }))
    ]
  }

  // Fallback to static options from filter definition
  if (filter.options && filter.options.length > 0) {
    return [
      allOption,
      ...filter.options.map((opt: { option_value: string; option_label: string }) => ({
        value: opt.option_value,
        label: opt.option_label
      }))
    ]
  }

  return [allOption]
}

/**
 * Check if a child filter should be disabled (no parent selected or no options)
 */
const isFilterDisabled = (filter: CategoryFilter): boolean => {
  const filterIndex = sortedFilters.value.findIndex(f => f.filter_key === filter.filter_key)

  // First filter is never disabled
  if (filterIndex === 0) return false

  // Check if parent filter has a value
  const parentFilter = sortedFilters.value[filterIndex - 1]
  const parentValue = props.modelValue[parentFilter.filter_key]

  if (!parentValue || parentValue === 'all' || parentValue === '') {
    return true // Parent not selected, disable child
  }

  // Check if there are options available
  const options = props.dynamicOptions?.[filter.filter_key]
  if (options && options.length === 0) {
    return true // No options available
  }

  return false
}

/**
 * Get placeholder text for disabled filter
 */
const getDisabledPlaceholder = (filter: CategoryFilter): string => {
  const filterIndex = sortedFilters.value.findIndex(f => f.filter_key === filter.filter_key)
  if (filterIndex > 0) {
    const parentFilter = sortedFilters.value[filterIndex - 1]
    return `Selecciona ${parentFilter.filter_label.toLowerCase()} primero`
  }
  return 'No disponible'
}

/**
 * Get current value for display
 */
const getFilterValue = (filterKey: string): string | string[] => {
  const value = props.modelValue[filterKey]
  if (value === null || value === undefined) return 'all'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return value as string | string[]
}

/**
 * Get range filter values [min, max]
 */
const getRangeValue = (filterKey: string): [number | null, number | null] => {
  const value = props.modelValue[filterKey]
  if (Array.isArray(value) && value.length === 2) {
    return [value[0] as number, value[1] as number]
  }
  return [null, null]
}

/**
 * Update range filter value
 */
const updateRangeFilter = (key: string, min: number | null, max: number | null) => {
  const newFilters = { ...props.modelValue }

  if ((min === null || min === undefined) && (max === null || max === undefined)) {
    delete newFilters[key]
  } else {
    newFilters[key] = [min ?? 0, max ?? 999999] as [number, number]
  }

  emit('update:modelValue', newFilters)
}
</script>

<template>
  <!-- Mobile Filter Button -->
  <div class="lg:hidden mb-4">
    <UButton
      block
      variant="outline"
      color="neutral"
      icon="i-heroicons-funnel"
      size="lg"
      @click="isDrawerOpen = true"
    >
      Filtros
      <UBadge
        v-if="hasActiveFilters"
        color="primary"
        size="xs"
        class="ml-2"
        :label="Object.keys(modelValue).length.toString()"
      />
    </UButton>
  </div>

  <!-- Desktop Filters -->
  <div class="hidden lg:block space-y-5">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h3 class="font-semibold text-gray-900">Filtros</h3>
      <UButton
        v-if="hasActiveFilters"
        variant="ghost"
        color="neutral"
        size="xs"
        @click="emit('clear')"
      >
        Limpiar
      </UButton>
    </div>

    <!-- Loading indicator -->
    <div v-if="loadingOptions" class="flex items-center gap-2 text-sm text-gray-500">
      <UIcon name="i-heroicons-arrow-path" class="w-4 h-4 animate-spin" />
      <span>Cargando opciones...</span>
    </div>

    <!-- Filter Controls -->
    <div class="space-y-4">
      <template v-for="filter in sortedFilters" :key="filter.id">
        <!-- Select Filter -->
        <UFormField
          v-if="filter.filter_type === 'select'"
          :label="filter.filter_label"
        >
          <USelect
            :model-value="getFilterValue(filter.filter_key)"
            :items="getFilterOptions(filter)"
            value-key="value"
            label-key="label"
            size="sm"
            :disabled="isFilterDisabled(filter)"
            :placeholder="isFilterDisabled(filter) ? getDisabledPlaceholder(filter) : undefined"
            @update:model-value="updateFilter(filter.filter_key, $event === 'all' ? null : $event)"
          />
          <p v-if="isFilterDisabled(filter)" class="text-xs text-gray-400 mt-1">
            {{ getDisabledPlaceholder(filter) }}
          </p>
        </UFormField>

        <!-- Multi-select Filter -->
        <UFormField
          v-else-if="filter.filter_type === 'multi-select'"
          :label="filter.filter_label"
        >
          <USelectMenu
            :model-value="(modelValue[filter.filter_key] as string[]) || []"
            :items="getFilterOptions(filter).slice(1)"
            value-key="value"
            label-key="label"
            multiple
            :placeholder="isFilterDisabled(filter) ? getDisabledPlaceholder(filter) : (filter.placeholder || 'Seleccionar...')"
            size="sm"
            :disabled="isFilterDisabled(filter)"
            @update:model-value="updateFilter(filter.filter_key, ($event as string[]).length ? $event : null)"
          />
        </UFormField>

        <!-- Boolean Filter -->
        <div
          v-else-if="filter.filter_type === 'boolean'"
          class="flex items-center gap-2"
        >
          <UCheckbox
            :model-value="!!modelValue[filter.filter_key]"
            :label="filter.filter_label"
            :disabled="isFilterDisabled(filter)"
            @update:model-value="updateFilter(filter.filter_key, $event || null)"
          />
        </div>

        <!-- Range Filter -->
        <UFormField
          v-else-if="filter.filter_type === 'range'"
          :label="filter.filter_label"
        >
          <div class="flex items-center gap-2">
            <UInput
              type="number"
              :model-value="getRangeValue(filter.filter_key)[0]"
              placeholder="Min"
              size="sm"
              class="w-20"
              :disabled="isFilterDisabled(filter)"
              @update:model-value="updateRangeFilter(filter.filter_key, $event as number | null, getRangeValue(filter.filter_key)[1])"
            />
            <span class="text-gray-400">-</span>
            <UInput
              type="number"
              :model-value="getRangeValue(filter.filter_key)[1]"
              placeholder="Max"
              size="sm"
              class="w-20"
              :disabled="isFilterDisabled(filter)"
              @update:model-value="updateRangeFilter(filter.filter_key, getRangeValue(filter.filter_key)[0], $event as number | null)"
            />
          </div>
        </UFormField>
      </template>
    </div>
  </div>

  <!-- Mobile Filter Drawer -->
  <USlideover
    v-model:open="isDrawerOpen"
    side="left"
    class="max-w-xs"
  >
    <template #header>
      <div class="flex items-center justify-between w-full">
        <h3 class="font-semibold text-lg">Filtros</h3>
        <UButton
          variant="ghost"
          color="neutral"
          icon="i-heroicons-x-mark"
          size="sm"
          @click="isDrawerOpen = false"
        />
      </div>
    </template>

    <template #body>
      <div class="space-y-5 p-4">
        <!-- Loading indicator -->
        <div v-if="loadingOptions" class="flex items-center gap-2 text-sm text-gray-500">
          <UIcon name="i-heroicons-arrow-path" class="w-4 h-4 animate-spin" />
          <span>Cargando opciones...</span>
        </div>

        <template v-for="filter in sortedFilters" :key="filter.id">
          <!-- Select Filter -->
          <UFormField
            v-if="filter.filter_type === 'select'"
            :label="filter.filter_label"
          >
            <USelect
              :model-value="getFilterValue(filter.filter_key)"
              :items="getFilterOptions(filter)"
              value-key="value"
              label-key="label"
              size="lg"
              :disabled="isFilterDisabled(filter)"
              @update:model-value="updateFilter(filter.filter_key, $event === 'all' ? null : $event)"
            />
            <p v-if="isFilterDisabled(filter)" class="text-xs text-gray-400 mt-1">
              {{ getDisabledPlaceholder(filter) }}
            </p>
          </UFormField>

          <!-- Multi-select Filter -->
          <UFormField
            v-else-if="filter.filter_type === 'multi-select'"
            :label="filter.filter_label"
          >
            <USelectMenu
              :model-value="(modelValue[filter.filter_key] as string[]) || []"
              :items="getFilterOptions(filter).slice(1)"
              value-key="value"
              label-key="label"
              multiple
              :placeholder="isFilterDisabled(filter) ? getDisabledPlaceholder(filter) : (filter.placeholder || 'Seleccionar...')"
              size="lg"
              :disabled="isFilterDisabled(filter)"
              @update:model-value="updateFilter(filter.filter_key, ($event as string[]).length ? $event : null)"
            />
          </UFormField>

          <!-- Boolean Filter -->
          <div
            v-else-if="filter.filter_type === 'boolean'"
            class="flex items-center gap-2"
          >
            <UCheckbox
              :model-value="!!modelValue[filter.filter_key]"
              :label="filter.filter_label"
              :disabled="isFilterDisabled(filter)"
              @update:model-value="updateFilter(filter.filter_key, $event || null)"
            />
          </div>

          <!-- Range Filter -->
          <UFormField
            v-else-if="filter.filter_type === 'range'"
            :label="filter.filter_label"
          >
            <div class="flex items-center gap-2">
              <UInput
                type="number"
                :model-value="getRangeValue(filter.filter_key)[0]"
                placeholder="Min"
                size="lg"
                class="flex-1"
                :disabled="isFilterDisabled(filter)"
                @update:model-value="updateRangeFilter(filter.filter_key, $event as number | null, getRangeValue(filter.filter_key)[1])"
              />
              <span class="text-gray-400">-</span>
              <UInput
                type="number"
                :model-value="getRangeValue(filter.filter_key)[1]"
                placeholder="Max"
                size="lg"
                class="flex-1"
                :disabled="isFilterDisabled(filter)"
                @update:model-value="updateRangeFilter(filter.filter_key, getRangeValue(filter.filter_key)[0], $event as number | null)"
              />
            </div>
          </UFormField>
        </template>
      </div>
    </template>

    <template #footer>
      <div class="flex gap-3 p-4 border-t border-gray-200">
        <UButton
          v-if="hasActiveFilters"
          variant="outline"
          color="neutral"
          class="flex-1"
          size="lg"
          @click="emit('clear')"
        >
          Limpiar
        </UButton>
        <UButton
          color="primary"
          class="flex-1"
          size="lg"
          @click="isDrawerOpen = false"
        >
          Ver resultados
        </UButton>
      </div>
    </template>
  </USlideover>
</template>
